import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();

  const navLinks = [
    { label: t.nav.process, href: '/#process' },
    { label: t.nav.faq, href: '/#faq' },
    { label: t.nav.contact, href: '/rendez-vous' },
  ];

  const handleNavClick = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const id = href.replace('/#', '');
      if (window.location.pathname !== '/') {
        window.location.href = href;
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-[#050505] text-white" data-testid="main-footer">
      <div className="container-custom py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand + Socials */}
          <div className="md:col-span-2">
            <img
              src="https://customer-assets.emergentagent.com/job_4c369902-603a-4e2e-b32f-237481e35eb7/artifacts/wjvsipix_Consultauto_Logo_Blanc.png"
              alt="Consultauto"
              className="w-[230px] md:w-[260px] h-auto mb-6"
              data-testid="footer-logo"
              loading="lazy"
              width={260}
              height={43}
            />
            <p className="text-sm font-sans text-white/80 leading-relaxed max-w-sm">
              {t.footer.description}
            </p>

            {/* Social Media Icons */}
            <div className="footer-brand-container" data-testid="footer-socials">
              <div className="footer-socials">
                <a href="https://www.facebook.com/consultauto.ca" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/consultauto.ca" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="2" y="2" width="20" height="20" rx="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="18" cy="6" r="1" />
                  </svg>
                </a>
                <a href="https://www.tiktok.com/@consultauto.ca" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M9 12a4 4 0 1 0 4 4V3c1 2 3 3 5 3" />
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/consultauto/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M16 8a6 6 0 0 1 6 6v6h-4v-6a2 2 0 0 0-4 0v6h-4v-12h4v2" />
                    <rect x="2" y="9" width="4" height="12" />
                    <circle cx="4" cy="4" r="2" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Links + Contact */}
          <div className="flex flex-col sm:flex-row md:flex-col gap-8">
            <div>
              <p className="text-xs font-sans font-medium uppercase tracking-[0.2em] text-white/80 mb-5" role="heading" aria-level="2">
                {t.footer.company}
              </p>
              <ul className="space-y-2.5">
                {navLinks.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      className="text-sm font-sans text-white/85 hover:text-white transition-colors duration-500"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-sans font-medium uppercase tracking-[0.2em] text-white/80 mb-5" role="heading" aria-level="2">
                Contact
              </p>
              <ul className="space-y-2.5 text-sm font-sans text-white/85">
                <li>
                  <a href="mailto:info@consultauto.ca" className="hover:text-white transition-colors duration-500" data-testid="footer-email">
                    {t.contact.info.email}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs font-sans text-white/75">{t.footer.copyright}</p>
          <div className="flex items-center gap-6">
            <Link to="/politique-de-confidentialite" className="text-xs font-sans text-white/75 hover:text-white transition-colors duration-500" data-testid="footer-privacy-link">
              {t.footer.privacy}
            </Link>
            <Link to="/conditions" className="text-xs font-sans text-white/75 hover:text-white transition-colors duration-500" data-testid="footer-terms-link">
              {t.footer.terms}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
